import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  FileCheck2,
  GraduationCap,
  Video,
  WandSparkles,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { getRoute } from "../utils/routes";

const content = {
  tr: {
    eyebrow: "İşe alım ve gelişim çözümleri",
    title: "Yapay zeka mülakatından çalışan eğitimine tek platform",
    intro:
      "HireThink; yapay zeka mülakatı, video mülakat, yapay zeka CV eşleştirme ve yapay zeka eğitim çözümleriyle insan kaynakları ekiplerinin aday seçimi ve çalışan gelişimi süreçlerini destekler.",
    action: "Detayları inceleyin",
    items: [
      {
        title: "Yapay zeka mülakatı",
        text: "Adaylarla standart kriterlere göre görüşün, cevapları analiz edin ve işe alım kararlarınız için karşılaştırılabilir içgörüler elde edin.",
        route: "premiumInterview",
        icon: WandSparkles,
      },
      {
        title: "Video mülakat",
        text: "Adayların kendi zamanlarında yanıtladığı videolu görüşmelerle ön değerlendirme sürecini esnek ve ölçeklenebilir hale getirin.",
        route: "standardInterview",
        icon: Video,
      },
      {
        title: "Yapay zeka CV eşleştirme",
        text: "Özgeçmişleri pozisyon kriterleriyle eşleştirin; uygun adayları CV skorlama ve analizle daha kolay belirleyin.",
        route: "cvScoring",
        icon: FileCheck2,
      },
      {
        title: "Yapay zeka eğitim",
        text: "Oryantasyon ve çalışan gelişimi için kişiselleştirilmiş eğitim planları, değerlendirme ve ilerleme takibi sunun.",
        route: "training",
        icon: GraduationCap,
      },
    ],
  },
  en: {
    eyebrow: "Hiring and development solutions",
    title: "From AI interviews to employee training in one platform",
    intro:
      "HireThink supports recruitment and employee development with AI interviews, video interviews, AI CV matching, and AI powered training.",
    action: "Explore solution",
    items: [
      {
        title: "AI interviews",
        text: "Assess candidates with consistent criteria and comparable insights.",
        route: "premiumInterview",
        icon: WandSparkles,
      },
      {
        title: "Video interviews",
        text: "Let candidates respond on their own time and scale early screening.",
        route: "standardInterview",
        icon: Video,
      },
      {
        title: "AI CV matching",
        text: "Match resumes to role criteria and identify relevant candidates faster.",
        route: "cvScoring",
        icon: FileCheck2,
      },
      {
        title: "AI training",
        text: "Support onboarding and employee growth with learning plans and progress tracking.",
        route: "training",
        icon: GraduationCap,
      },
    ],
  },
  cs: {
    eyebrow: "Nábor a rozvoj",
    title: "Od AI pohovorů po vzdělávání zaměstnanců",
    intro:
      "HireThink podporuje nábor a rozvoj zaměstnanců pomocí AI pohovorů, video pohovorů, párování životopisů a školení s umělou inteligencí.",
    action: "Prozkoumat řešení",
    items: [
      {
        title: "AI pohovory",
        text: "Hodnoťte uchazeče podle jednotných kritérií.",
        route: "premiumInterview",
        icon: WandSparkles,
      },
      {
        title: "Video pohovory",
        text: "Umožněte uchazečům odpovídat ve vlastním čase.",
        route: "standardInterview",
        icon: Video,
      },
      {
        title: "Párování životopisů",
        text: "Porovnávejte životopisy s požadavky pozice.",
        route: "cvScoring",
        icon: FileCheck2,
      },
      {
        title: "AI školení",
        text: "Podpořte adaptaci a rozvoj zaměstnanců.",
        route: "training",
        icon: GraduationCap,
      },
    ],
  },
};

const SearchSolutions = () => {
  const { i18n } = useTranslation();
  const language = i18n.language?.slice(0, 2) || "tr";
  const copy = content[language] || content.tr;

  return (
    <section
      className="bg-white py-20 md:py-24"
      aria-labelledby="ai-solutions-heading"
    >
      <div className="container-custom px-4 sm:px-6">
        <div className="max-w-4xl">
          <p className="text-xs font-bold uppercase tracking-[.2em] text-teal-700">
            {copy.eyebrow}
          </p>
          <h2
            id="ai-solutions-heading"
            className="mt-4 text-3xl md:text-5xl font-heading font-semibold leading-tight text-slate-900"
          >
            {copy.title}
          </h2>
          <p className="mt-5 text-lg leading-relaxed text-slate-600">
            {copy.intro}
          </p>
        </div>
        <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
          {copy.items.map(({ title, text, route, icon: Icon }) => (
            <article
              key={title}
              className="flex flex-col rounded-2xl border border-slate-200 bg-[#f7fafb] p-6"
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#e1f5f0] text-teal-800">
                <Icon size={24} />
              </div>
              <h3 className="mt-6 text-xl font-semibold text-slate-900">
                {title}
              </h3>
              <p className="mt-3 flex-1 text-sm leading-relaxed text-slate-600">
                {text}
              </p>
              <Link
                to={getRoute(route, language)}
                className="mt-6 inline-flex items-center gap-1.5 self-start text-sm font-semibold text-[#255c9b] hover:underline"
              >
                {copy.action}
                <ArrowUpRight size={16} />
              </Link>
            </article>
          ))}
        </div>
        {language === "tr" && (
          <div className="mt-8 border-t border-slate-200 pt-6">
            <Link to={getRoute('aiSolutions')} className="inline-flex items-center gap-2 font-semibold text-teal-800 hover:underline">
              Satış, finans, üretim ve müşteri iletişimi için tüm yapay zeka çözümleri
              <ArrowUpRight size={18} aria-hidden="true" />
            </Link>
          </div>
        )}
      </div>
    </section>
  );
};

export default SearchSolutions;
